import styled from "styled-components";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { H3 } from "@/common/elements";
import { BsDot } from "react-icons/bs";
import { Button } from "@/common/components/button";
import { BiEdit } from "react-icons/bi";
import { FiDelete } from "react-icons/fi";
import { CiSettings } from "react-icons/ci";
import { Modal } from "@/common/components/modal";
import { COMPANY_ADDRESS_URL } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useState } from "react";
import { Prompt } from "@/common/components/prompt";
import Form from "@/common/components/form/Form";
import useQuery from "@/hooks/useQuery";
import { handleEditFormData } from "./data";

const AddressCards = styled.div`
  width: 100%;
  padding: 1rem;
  background-color: ${({ theme }) => theme.bg.prim_500};
  margin-bottom: 1rem;

  .address-wrapper {
    display: flex;
    align-items: start;
    gap: 1rem;

    .icon-wrapper {
      background-color: ${({ theme }) => theme.bg.prim_400};
      padding: 0.5rem;
    }

    .default {
      background-color: ${({ theme }) => theme.bg.secondary};
      color: ${({ theme }) => theme.text.prim_500};
      border-radius: 4rem;
      padding: 0.1rem 0.5rem;
      display: flex;
      align-items: center;
    }

    .default svg {
      color: ${({ theme }) => theme.bg.prim_500};
      width: 2rem;
      height: 2rem;
    }
  }
`;

interface IAddressCardProps {
  id: string;
  building_number: string;
  street: string;
  city: string;
  state: string;
  lga: string;
  country: string;
  landmark: string;
  is_default?: boolean;
  postal_code?: string;
  address_line_one?: string;
  address_line_two?: string;
}

enum EAddressAction {
  DELETE = "delete",
  SET_DEFAULT = "set_default",
}

const AddressCard = ({
  refetch,
  index,
  address,
}: {
  refetch: () => void;
  index: number;
  address: IAddressCardProps;
}) => {
  const makeRequest = useApiCallHandling();
  const [confirmModal, setConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<EAddressAction | null>(
    null,
  );
  const { data: addressData } = useQuery(
    COMPANY_ADDRESS_URL + `/${address?.id}`,
  );

  const handleMutateAddress = () => {
    setLoading(true);
    makeRequest(
      currentAction === EAddressAction.SET_DEFAULT
        ? COMPANY_ADDRESS_URL + `/${address?.id}/default`
        : COMPANY_ADDRESS_URL,
      currentAction === EAddressAction.SET_DEFAULT ? "PUT" : "PATCH",
      currentAction === EAddressAction.SET_DEFAULT
        ? {}
        : { address_ids: [address?.id] },
      () => refetch(),
      () => console.log,
      () => {
        setLoading(false);
        setConfirmModal(false);
      },
    );
  };

  const handleEdit = (data: any) => {
    setLoading(true);
    makeRequest(
      COMPANY_ADDRESS_URL + `/${address?.id}`,
      "PUT",
      data,
      () => refetch(),
      () => console.log,
      () => {
        setLoading(false);
        setShowEditModal(false);
      },
    );
  };

  const handleAction = (action: EAddressAction) => {
    setCurrentAction(action);
    setConfirmModal(true);
  };

  return (
    <AddressCards>
      <div className="address-wrapper">
        <div className="icon-wrapper">
          <HiOutlineBuildingOffice2 size={30} />
        </div>
        <div className="address-details">
          <div className="flex items-center gap-4 mb-4">
            <H3>Address {index}</H3>
            {address?.is_default && (
              <span className="default">
                <BsDot /> Default
              </span>
            )}
          </div>
          <p>{address?.address_line_one}</p>
          <p>{address?.address_line_two}</p>
          <p>
            {address?.building_number} {address?.street}, {address?.city},{" "}
          </p>
          <p>{address?.state}</p>
          <p>{address?.country}</p>
          <p>{address?.landmark}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4 justify-end">
        <Button onClick={() => setShowEditModal(true)} secondary>
          <BiEdit /> Edit
        </Button>
        <Button secondary onClick={() => handleAction(EAddressAction.DELETE)}>
          <FiDelete /> Delete
        </Button>
        {!address?.is_default && (
          <Button onClick={() => handleAction(EAddressAction.SET_DEFAULT)}>
            <CiSettings /> Set as Default
          </Button>
        )}
      </div>
      <Modal
        title={
          currentAction === EAddressAction.SET_DEFAULT
            ? "Set as Default Address"
            : "Delete Address"
        }
        isOpen={confirmModal}
        close={() => {
          setConfirmModal(false);
        }}
      >
        <Prompt
          prompt={
            currentAction === EAddressAction.SET_DEFAULT
              ? `Are you sure you want to set this as your default address?`
              : `Are you sure you want to delete this address?`
          }
          type={"question"}
          onMainAction={handleMutateAddress}
          onSecondaryAction={() => setConfirmModal(false)}
          mainLoading={loading}
        />
      </Modal>
      <Modal
        isOpen={showEditModal}
        close={() => setShowEditModal(false)}
        title="Edit Address"
      >
        <div className="modal max-w-[900px] md:w-[700px]">
          <Form
            formInputs={handleEditFormData(addressData)}
            processInputs={handleEdit}
            loadingState={loading}
          />
        </div>
      </Modal>
    </AddressCards>
  );
};

export default AddressCard;
